import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeftRight, Plus } from 'lucide-react';
import { AccessDenied, errorStatus } from '@/components/RouteError';
import { usePageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { MovementsTable } from '@/components/finance/MovementsTable';
import { MovementForm } from '@/components/finance/MovementForm';
import { TransferForm } from '@/components/finance/TransferForm';
import { apartmentsQuery } from '@/lib/apartments';
import { errorMessage } from '@/lib/errors';
import {
  financeAccountsQuery,
  financeCategoriesQuery,
  financeCounterpartiesQuery,
  financeMovementsQuery,
  financeSettingsQuery,
} from '@/lib/finance';
import { usePermissions } from '@/lib/me';

const PAGE = 50;

export const Route = createFileRoute('/_app/finance/movements/')({
  component: MovementsPage,
});

function MovementsPage() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  // Filters live in component state, like every other list in the SPA. They
  // are not in the URL, so a filtered feed is not a shareable link yet.
  const [accountId, setAccountId] = useState('');
  const [direction, setDirection] = useState<'' | 'in' | 'out'>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [includeReversed, setIncludeReversed] = useState(false);
  const [offset, setOffset] = useState(0);
  const [newPayment, setNewPayment] = useState(false);
  const [newTransfer, setNewTransfer] = useState(false);

  const accounts = useQuery(financeAccountsQuery(true));
  const categories = useQuery(financeCategoriesQuery(true));
  const counterparties = useQuery(financeCounterpartiesQuery());
  const apartments = useQuery(apartmentsQuery);
  const settings = useQuery(financeSettingsQuery);

  // The server answers 400 when from is after to rather than an empty page,
  // so the request is not sent for a window nobody meant.
  const reversedWindow = !!from && !!to && from > to;
  const movements = useQuery({
    ...financeMovementsQuery({
      accountId: accountId || undefined,
      direction: direction || undefined,
      from: from || undefined,
      to: to || undefined,
      includeReversed,
      limit: PAGE,
      offset,
    }),
    enabled: !reversedWindow,
  });

  const total = movements.data?.total ?? 0;

  usePageHeader({
    subtitle: movements.data ? `${total} payments` : undefined,
    actions: can('finance.confirm') ? (
      <span className="flex gap-2">
        <Button variant="secondary" size="sm" onClick={() => setNewTransfer(true)}>
          <ArrowLeftRight className="h-4 w-4" />
          Transfer
        </Button>
        <Button variant="primary" size="sm" onClick={() => setNewPayment(true)}>
          <Plus className="h-4 w-4" />
          New payment
        </Button>
      </span>
    ) : undefined,
  });

  if (errorStatus(movements.error) === 403) return <AccessDenied />;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end gap-4">
        <Select
          label="Account"
          value={accountId}
          // Enabled empty option, not a `placeholder`: Select's placeholder
          // is a DISABLED option (ui/select.tsx), so a filter set once could
          // never be cleared without a reload.
          options={[
            { value: '', label: 'Every account' },
            ...(accounts.data ?? []).map((a) => ({ value: a.id, label: a.name })),
          ]}
          onChange={(e) => {
            setAccountId(e.target.value);
            setOffset(0);
          }}
        />
        <Select
          label="Direction"
          value={direction}
          options={[
            { value: '', label: 'Both' },
            { value: 'in', label: 'In' },
            { value: 'out', label: 'Out' },
          ]}
          onChange={(e) => {
            setDirection(e.target.value as '' | 'in' | 'out');
            setOffset(0);
          }}
        />
        <Input
          label="From"
          type="date"
          value={from}
          onChange={(e) => {
            setFrom(e.target.value);
            setOffset(0);
          }}
        />
        <Input
          label="To"
          type="date"
          value={to}
          onChange={(e) => {
            setTo(e.target.value);
            setOffset(0);
          }}
        />
        <Checkbox
          label="Include reversed pairs"
          checked={includeReversed}
          onChange={(e) => {
            setIncludeReversed(e.target.checked);
            setOffset(0);
          }}
        />
      </div>

      {reversedWindow ? (
        <p role="alert" className="text-sm text-destructive">
          The window runs backwards — From is after To.
        </p>
      ) : movements.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {errorMessage(movements.error)}
        </p>
      ) : movements.isLoading ? (
        <p className="text-sm text-muted">Loading payments…</p>
      ) : (
        <>
          <MovementsTable
            movements={movements.data?.items ?? []}
            accounts={accounts.data}
            categories={categories.data}
            onOpen={(id) => void navigate({ to: '/finance/movements/$id', params: { id } })}
          />
          <div className="flex items-center gap-3 text-sm">
            <Button
              variant="secondary"
              size="sm"
              disabled={offset === 0}
              onClick={() => setOffset((o) => Math.max(0, o - PAGE))}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={offset + PAGE >= total}
              onClick={() => setOffset((o) => o + PAGE)}
            >
              Next
            </Button>
            <span className="text-muted">
              {total === 0 ? 'none' : `${offset + 1}–${Math.min(offset + PAGE, total)} of ${total}`}
            </span>
          </div>
        </>
      )}

      {newPayment && (
        <Dialog open side onClose={() => setNewPayment(false)} title="New payment">
          <MovementForm
            accounts={(accounts.data ?? []).filter((a) => a.is_active)}
            categories={categories.data ?? []}
            counterparties={counterparties.data}
            apartments={apartments.data}
            businessTz={settings.data?.business_tz}
            canManage={can('finance.manage')}
            onDone={() => setNewPayment(false)}
            onCancel={() => setNewPayment(false)}
          />
        </Dialog>
      )}
      {newTransfer && (
        <Dialog open side onClose={() => setNewTransfer(false)} title="Transfer between accounts">
          <TransferForm
            accounts={(accounts.data ?? []).filter((a) => a.is_active)}
            categories={categories.data ?? []}
            businessTz={settings.data?.business_tz}
            onDone={() => setNewTransfer(false)}
            onCancel={() => setNewTransfer(false)}
          />
        </Dialog>
      )}
    </div>
  );
}
